import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { StatCard } from '@/client/components/common/StatCard';
import { TYPE_META } from '@/client/components/timeoff/constants';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { getMonthRange, getMonthWeekBuckets, getWeekRange, toISODate } from '@/client/lib/dates';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FolderKanban,
  Palmtree,
  Plus,
} from 'lucide-react';
import { useState } from 'react';

const WEEK_TARGET = 40;
const DAY_TARGET = 8;
// Signal first, then muted companions that sit quietly beside Graphite and Paper.
const PROJECT_COLORS = ['#19C39C', '#8D9390', '#5B8DEF', '#E0A33A', '#C77DDB', '#E0705A'];

function getGreeting(name?: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return `Good ${part}${name ? `, ${name.split(' ')[0]}` : ''}`;
}

function fmtH(h: number) {
  return `${Number.isInteger(h) ? h : h.toFixed(1)}h`;
}

function parseISO(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function shortDate(iso: string) {
  return parseISO(iso).toLocaleDateString('en', { day: 'numeric', month: 'short' });
}

/** Weekdays (Mon–Fri) between two ISO dates, inclusive. */
function countWeekdays(fromIso: string, toIso: string) {
  let n = 0;
  const d = parseISO(fromIso);
  const end = parseISO(toIso);
  while (d <= end) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) n++;
    d.setDate(d.getDate() + 1);
  }
  return n;
}

function SectionCard({
  title,
  subtitle,
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('flex flex-col overflow-hidden', className)}>
      <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold tracking-tight">{title}</p>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="flex-1">{children}</div>
    </Card>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-5 py-8 text-center text-sm text-muted-foreground">{children}</p>;
}

export function UserDashboard() {
  const [monthOffset, setMonthOffset] = useState(0);
  const todayIso = toISODate(new Date());

  const { data: me } = trpc.user.me.useQuery();
  const range = getMonthRange(monthOffset);
  const week = getWeekRange(0);
  const lastWeek = getWeekRange(-1);
  const [yearStr, monthStr] = range.from.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  const { data: entries = [], isLoading } = trpc.timesheets.listEntries.useQuery({
    date_from: range.from,
    date_to: range.to,
  });
  const { data: weekEntries = [] } = trpc.timesheets.listEntries.useQuery({
    date_from: week.from,
    date_to: week.to,
  });
  const { data: submissions = [] } = trpc.timesheets.listSubmissions.useQuery({});
  const { data: myProjects = [] } = trpc.projects.list.useQuery();
  const { data: balance = [] } = trpc.timeoff.myBalance.useQuery();
  const { data: offThisMonth = [] } = trpc.timeoff.list.useQuery({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const { data: offNextMonth = [] } = trpc.timeoff.list.useQuery({
    year: nextMonth.getFullYear(),
    month: nextMonth.getMonth() + 1,
  });

  // ── Month numbers ────────────────────────────────────────────────────────
  const totalHours = entries.reduce((s, e) => s + e.hours, 0);
  const isCurrentMonth = monthOffset === 0;
  const isFutureMonth = monthOffset > 0;
  const expectedTo = isCurrentMonth ? todayIso : range.to;
  const expectedHours = isFutureMonth ? 0 : countWeekdays(range.from, expectedTo) * DAY_TARGET;
  const monthTargetHours = countWeekdays(range.from, range.to) * DAY_TARGET;
  const pace = expectedHours > 0 ? totalHours / expectedHours : 0;
  const daysLogged = new Set(entries.map((e) => e.date)).size;

  // ── This week ────────────────────────────────────────────────────────────
  const weekHours = weekEntries.reduce((s, e) => s + e.hours, 0);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = parseISO(week.from);
    d.setDate(d.getDate() + i);
    const iso = toISODate(d);
    const hours = weekEntries.filter((e) => e.date === iso).reduce((s, e) => s + e.hours, 0);
    const weekend = i >= 5;
    return {
      iso,
      label: d.toLocaleDateString('en', { weekday: 'short' }),
      day: d.getDate(),
      hours,
      weekend,
      isToday: iso === todayIso,
      isFuture: iso > todayIso,
      missing: !weekend && iso < todayIso && hours === 0,
    };
  });
  const missingDays = weekDays.filter((d) => d.missing).length;
  const maxDayHours = Math.max(DAY_TARGET, ...weekDays.map((d) => d.hours));
  const lastWeekSubmitted = submissions.some(
    (s) => s.date_from <= lastWeek.from && s.date_to >= lastWeek.to,
  );

  // ── Projects ─────────────────────────────────────────────────────────────
  const byProject = Object.values(
    entries.reduce<Record<string, { name: string; hours: number }>>((acc, e) => {
      acc[e.project_id] ??= { name: e.project_name, hours: 0 };
      acc[e.project_id].hours += e.hours;
      return acc;
    }, {}),
  ).sort((a, b) => b.hours - a.hours);
  const activeProjects = myProjects.filter((p) => p.status === 'active');

  // ── Weeks of the month ───────────────────────────────────────────────────
  const weekRows = getMonthWeekBuckets(year, month).map((b) => ({
    label: b.from === b.to ? shortDate(b.from) : b.label,
    isCurrent: b.from <= todayIso && todayIso <= b.to,
    hours: entries
      .filter((e) => e.date >= b.from && e.date <= b.to)
      .reduce((s, e) => s + e.hours, 0),
  }));

  // ── Time off ─────────────────────────────────────────────────────────────
  const allOff = [...offThisMonth, ...offNextMonth].filter(
    (r, i, arr) => arr.findIndex((x) => x.id === r.id) === i,
  );
  const myUpcoming = allOff
    .filter((r) => r.user_id === me?.id && r.date_to >= todayIso && r.status !== 'rejected')
    .sort((a, b) => a.date_from.localeCompare(b.date_from))
    .slice(0, 3);
  const teamOutThisWeek = allOff.filter(
    (r) =>
      r.user_id !== me?.id &&
      r.status === 'approved' &&
      r.date_from <= week.to &&
      r.date_to >= week.from,
  );
  const vacation = balance.find((b) => b.type === 'vacation');

  const recent = [...entries]
    .sort((a, b) => b.date.localeCompare(a.date) || b.created_at.localeCompare(a.created_at))
    .slice(0, 6);

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1240px] space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border bg-card px-1 shadow-xs">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonthOffset((o) => o - 1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[130px] text-center text-sm font-medium">{range.label}</span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setMonthOffset((o) => o + 1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {monthOffset !== 0 && (
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(0)}>
            This month
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/timeoff">
              <CalendarRange className="h-4 w-4" />
              <span className="hidden sm:inline">Request time off</span>
            </Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/timesheets">
              <Plus className="h-4 w-4" />
              Log time
            </Link>
          </Button>
        </div>
      </div>

      {/* Headline */}
      <div className="flex flex-col gap-5 border-b pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {getGreeting(me?.full_name ?? undefined)} · {range.label}
          </p>
          <h1 className="mt-2 text-[34px] font-semibold leading-[1.05] tracking-[-0.025em] sm:text-[44px]">
            {isLoading ? '—' : fmtH(totalHours)}
            <span className="ml-2 text-lg font-medium tracking-normal text-muted-foreground sm:text-xl">
              logged {isCurrentMonth ? 'so far' : 'this month'}
            </span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isFutureMonth
              ? `This month has ${monthTargetHours}h of working time.`
              : expectedHours === 0
                ? 'No working days yet this month.'
                : pace >= 1
                  ? `You're on track — ${fmtH(totalHours)} of ${fmtH(expectedHours)} expected by ${isCurrentMonth ? 'today' : 'month end'}.`
                  : `${fmtH(Math.max(expectedHours - totalHours, 0))} behind the ${fmtH(expectedHours)} expected by ${isCurrentMonth ? 'today' : 'month end'}.`}
          </p>
        </div>
        <div className="w-full max-w-sm shrink-0 lg:w-80">
          <div className="mb-1.5 flex justify-between text-xs text-muted-foreground">
            <span>Month progress</span>
            <span className="tnum">
              {fmtH(totalHours)} / {fmtH(monthTargetHours)}
            </span>
          </div>
          <div className="relative h-2.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand-gradient transition-[width] duration-700"
              style={{
                width: `${Math.min((totalHours / Math.max(monthTargetHours, 1)) * 100, 100)}%`,
              }}
            />
            {!isFutureMonth && monthTargetHours > 0 && (
              <div
                title="Expected by today"
                className="absolute top-0 h-full w-0.5 bg-foreground/40"
                style={{ left: `${Math.min((expectedHours / monthTargetHours) * 100, 100)}%` }}
              />
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="This week"
          value={fmtH(weekHours)}
          hint={`of ${WEEK_TARGET}h target`}
          icon={Clock}
          tone={weekHours >= WEEK_TARGET ? 'good' : 'primary'}
        />
        <StatCard
          label={isCurrentMonth ? 'This month' : range.label.split(' ')[0]}
          value={fmtH(totalHours)}
          hint={`${daysLogged} day${daysLogged === 1 ? '' : 's'} logged`}
          icon={CalendarDays}
          tone="primary"
        />
        <StatCard
          label="Vacation left"
          value={vacation ? `${vacation.total - vacation.used}d` : '—'}
          hint={vacation ? `${vacation.used} of ${vacation.total} used` : undefined}
          icon={Palmtree}
          tone="good"
        />
        <StatCard
          label="Active projects"
          value={activeProjects.length}
          hint={
            activeProjects.length
              ? activeProjects
                  .slice(0, 2)
                  .map((p) => p.name)
                  .join(', ') + (activeProjects.length > 2 ? '…' : '')
              : 'None assigned'
          }
          icon={FolderKanban}
          tone="warn"
        />
      </div>

      {/* Week + projects */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.35fr_1fr]">
        <SectionCard
          title="This week"
          subtitle={week.label}
          action={
            <Link
              to="/timesheets"
              className="inline-flex items-center gap-1 text-xs font-medium text-signal-ink hover:underline"
            >
              Timesheet <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <div className="px-5 pt-5">
            <div className="grid h-40 grid-cols-7 items-end gap-2 sm:gap-3">
              {weekDays.map((d) => (
                <div key={d.iso} className="flex h-full flex-col items-center justify-end gap-1.5">
                  <span
                    className={cn(
                      'tnum text-[11px] font-medium',
                      d.hours ? 'text-foreground' : 'text-muted-foreground/60',
                    )}
                  >
                    {d.hours ? fmtH(d.hours) : d.missing ? '!' : ''}
                  </span>
                  <div
                    className={cn(
                      'w-full max-w-[44px] rounded-md transition-[height] duration-500',
                      d.hours === 0 && 'h-1',
                      d.weekend || (d.isFuture && d.hours === 0)
                        ? 'bg-muted-foreground/20'
                        : 'bg-brand-gradient',
                      d.missing && 'bg-warn/40',
                      d.isToday && 'ring-2 ring-signal/40 ring-offset-2 ring-offset-card',
                    )}
                    style={d.hours ? { height: `${(d.hours / maxDayHours) * 100}%` } : undefined}
                  />
                </div>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-7 gap-2 border-t pt-2 sm:gap-3">
              {weekDays.map((d) => (
                <div key={d.iso} className="text-center">
                  <p
                    className={cn(
                      'text-[11px] font-medium',
                      d.isToday ? 'text-signal-ink' : 'text-muted-foreground',
                    )}
                  >
                    {d.label}
                  </p>
                  <p className={cn('text-xs', d.isToday && 'font-semibold text-signal-ink')}>
                    {d.day}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 space-y-2 px-5 pb-5">
            {missingDays > 0 ? (
              <div className="flex items-center gap-2.5 rounded-lg bg-warn-bg px-3 py-2.5 text-sm text-warn">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  {missingDays} weekday{missingDays === 1 ? '' : 's'} this week without logged
                  hours.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 rounded-lg bg-good-bg px-3 py-2.5 text-sm text-good">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>All weekdays so far are logged.</span>
              </div>
            )}
            {!lastWeekSubmitted && (
              <Link
                to="/timesheets"
                className="flex items-center gap-2.5 rounded-lg border border-signal/25 bg-signal/[0.06] px-3 py-2.5 text-sm text-signal-ink transition-colors hover:bg-signal/10"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="flex-1">Last week ({lastWeek.label}) isn't submitted yet.</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Hours by project" subtitle={range.label}>
          {byProject.length === 0 ? (
            <Empty>No hours logged this month.</Empty>
          ) : (
            <div className="space-y-4 p-5">
              <div className="flex h-2.5 overflow-hidden rounded-full bg-muted">
                {byProject.map((p, i) => (
                  <div
                    key={p.name}
                    title={`${p.name}: ${fmtH(p.hours)}`}
                    style={{
                      width: `${(p.hours / totalHours) * 100}%`,
                      background: PROJECT_COLORS[i % PROJECT_COLORS.length],
                    }}
                  />
                ))}
              </div>
              <ul className="space-y-3">
                {byProject.map((p, i) => (
                  <li key={p.name} className="flex items-center gap-3 text-sm">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: PROJECT_COLORS[i % PROJECT_COLORS.length] }}
                    />
                    <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                    <span className="tnum text-muted-foreground">
                      {Math.round((p.hours / totalHours) * 100)}%
                    </span>
                    <span className="tnum w-14 text-right font-semibold">{fmtH(p.hours)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {myProjects.length > 0 && (
            <div className="border-t px-5 py-4">
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Your projects
              </p>
              <ul className="space-y-1">
                {myProjects.map((p) => (
                  <li key={p.id}>
                    <Link
                      to="/projects/$id"
                      params={{ id: p.id }}
                      className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-[10px] font-semibold text-muted-foreground group-hover:bg-card">
                        {p.name
                          .split(' ')
                          .map((w) => w[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{p.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {p.client_name ?? '—'} · {p.member_count} member
                          {p.member_count === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                          p.status === 'active' && 'bg-good-bg text-good',
                          p.status === 'on_hold' && 'bg-warn-bg text-warn',
                          p.status === 'completed' && 'bg-muted text-muted-foreground',
                        )}
                      >
                        {p.status === 'on_hold'
                          ? 'On hold'
                          : p.status[0].toUpperCase() + p.status.slice(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Weeks + time off */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Month by week" subtitle={`Target ${WEEK_TARGET}h per week`}>
          <div>
            {weekRows.map((row) => {
              const pct = Math.min((row.hours / WEEK_TARGET) * 100, 100);
              const atTarget = row.hours >= WEEK_TARGET;
              return (
                <div
                  key={row.label}
                  className={cn(
                    'grid grid-cols-[minmax(0,110px)_1fr_52px] items-center gap-4 border-b px-5 py-3 last:border-0',
                    row.isCurrent && isCurrentMonth && 'bg-signal/[0.05]',
                  )}
                >
                  <span
                    className={cn(
                      'truncate text-sm',
                      row.isCurrent && isCurrentMonth
                        ? 'font-medium text-signal-ink'
                        : 'text-muted-foreground',
                    )}
                  >
                    {row.label}
                  </span>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', atTarget ? 'bg-good' : 'bg-signal')}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span
                    className={cn('tnum text-right text-sm font-semibold', atTarget && 'text-good')}
                  >
                    {row.hours > 0 ? fmtH(row.hours) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard
          title="Time off"
          subtitle={`Balance ${now.getFullYear()}`}
          action={
            <Link
              to="/timeoff"
              className="inline-flex items-center gap-1 text-xs font-medium text-signal-ink hover:underline"
            >
              Calendar <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <div className="space-y-5 p-5">
            <div className="grid grid-cols-3 gap-3">
              {balance.map((b) => {
                const meta = TYPE_META[b.type];
                const left = b.total - b.used;
                return (
                  <div key={b.type} className="rounded-lg border bg-muted/30 p-3">
                    <p className="truncate text-[11px] font-medium text-muted-foreground">
                      {meta?.label ?? b.type}
                    </p>
                    <p className="tnum mt-1 text-lg font-semibold leading-none">
                      {left}
                      <span className="text-xs font-normal text-muted-foreground">
                        {' '}
                        / {b.total}
                      </span>
                    </p>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(b.used / Math.max(b.total, 1)) * 100}%`,
                          background: meta?.color ?? 'hsl(var(--primary))',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Your upcoming
              </p>
              {myUpcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing planned.</p>
              ) : (
                <ul className="space-y-2">
                  {myUpcoming.map((r) => {
                    const meta = TYPE_META[r.type];
                    return (
                      <li key={r.id} className="flex items-center gap-2.5 text-sm">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: meta?.color }}
                        />
                        <span className="font-medium">{meta?.label ?? r.type}</span>
                        <span className="text-muted-foreground">
                          {shortDate(r.date_from)}
                          {r.date_to !== r.date_from && ` – ${shortDate(r.date_to)}`}
                        </span>
                        <span
                          className={cn(
                            'ml-auto rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
                            r.status === 'approved'
                              ? 'bg-good-bg text-good'
                              : 'bg-warn-bg text-warn',
                          )}
                        >
                          {r.status}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Team out this week
              </p>
              {teamOutThisWeek.length === 0 ? (
                <p className="text-sm text-muted-foreground">Everyone's in.</p>
              ) : (
                <ul className="space-y-2">
                  {teamOutThisWeek.map((r) => (
                    <li key={r.id} className="flex items-center gap-2.5 text-sm">
                      <InitialsAvatar name={r.user.full_name ?? '?'} size={22} />
                      <span className="truncate font-medium">{r.user.full_name}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {TYPE_META[r.type]?.label ?? r.type} · {shortDate(r.date_from)}
                        {r.date_to !== r.date_from && ` – ${shortDate(r.date_to)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Recent entries */}
      <SectionCard
        title="Recent entries"
        subtitle={`Latest work logged in ${range.label}`}
        action={
          <Link
            to="/timesheets"
            className="inline-flex items-center gap-1 text-xs font-medium text-signal-ink hover:underline"
          >
            All entries <ArrowRight className="h-3 w-3" />
          </Link>
        }
      >
        {recent.length === 0 ? (
          <Empty>No entries yet — log your first hours from the timesheet.</Empty>
        ) : (
          <ul className="divide-y">
            {recent.map((e) => {
              const color =
                PROJECT_COLORS[
                  Math.max(
                    byProject.findIndex((p) => p.name === e.project_name),
                    0,
                  ) % PROJECT_COLORS.length
                ];
              return (
                <li key={e.id} className="flex items-center gap-4 px-5 py-3 text-sm">
                  <div className="w-12 shrink-0 text-center">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground">
                      {parseISO(e.date).toLocaleDateString('en', { weekday: 'short' })}
                    </p>
                    <p className="text-base font-semibold leading-tight">
                      {parseISO(e.date).getDate()}
                    </p>
                  </div>
                  <span className="h-8 w-1 shrink-0 rounded-full" style={{ background: color }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{e.project_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{e.description}</p>
                  </div>
                  <span className="tnum shrink-0 font-semibold">{fmtH(e.hours)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
